import { c as _c } from "react/compiler-runtime";
import React, { useContext, useState } from 'react';
import { IgnoredEvent, IgnoredEventCause } from '../../../../typings/IgnoredEvent.interface';
import { Request, RequestRejectionCause } from '../../../../typings/Request.interface';
import { APISource } from '../../../../typings/Source.interface';
import Button from '../components/common/base/Button';
import Text from '../components/common/base/Text';
import { Div } from '../components/common/helpers/StyledUtils';
import { useToasts } from '../components/common/Toast';
import { GlobalContext } from '../components/contexts/GlobalContext';
export const rejection_cause_labels = {
  SOURCE_DISABLED: 'Source Disabled',
  NO_CONNECTION: 'No Connection',
  VERIFICATION_FAILED: 'Authentication Failed',
  UNSUPPORTED_HTTP_METHOD: 'Unsupported Method',
  UNSUPPORTED_CONTENT_TYPE: 'Unsupported Content-Type',
  UNPARSABLE_JSON: 'Unparsable JSON',
  PAYLOAD_TOO_LARGE: 'Payload Too large',
  INGESTION_FATAL: 'Ingestion Fatal Error',
  UNKNOWN: 'Unknown error'
};
export const cause_filter_breakdown: Record<IgnoredEventCause, (ignored_event: IgnoredEvent) => {
  label: string;
  text: string;
}> = {
  DISABLED: () => ({
    label: 'Connection Disabled',
    text: 'The connection was disabled at the time the request was received.'
  }),
  FILTERED: ignored_event => ({
    label: 'Filtered',
    text: `This request was filtered on ${(ignored_event.meta as string[]).join(', ')} by a filter rule on this connection.`
  }),
  TRANSFORMATION_FAILED: () => ({
    label: 'Transformation Failed',
    text: `The transformation failed to execute and resulted in a fatal error.`
  }),
  CLI_DISCONNECTED: () => ({
    label: 'CLI Offline',
    text: `No CLI was online when the request was received.`
  }),
  DUPLICATE: () => ({
    label: 'Duplicate',
    text: `This event was identified as a duplicate and ignored based on the deduplicate rule.`
  })
};

// Action button components that use hooks
const EnableSourceButton: React.FC<{
  source: APISource;
  show_action: boolean;
}> = t0 => {
  const $ = _c(7);
  const {
    source,
    show_action
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const [is_updating, setIsUpdating] = useState(false);
  if (!show_action) {
    return null;
  }
  let t1;
  if ($[0] !== HookdeckAPI.sources || $[1] !== addToast || $[2] !== source.id) {
    t1 = () => {
      setIsUpdating(true);
      HookdeckAPI.sources.enable(source.id).then(() => {
        addToast("success", "Source enabled successfully");
      }).finally(() => {
        setIsUpdating(false);
      });
    };
    $[0] = HookdeckAPI.sources;
    $[1] = addToast;
    $[2] = source.id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== is_updating || $[5] !== t1) {
    t2 = <Button outline={true} disabled={is_updating} onClick={t1}>Enable source</Button>;
    $[4] = is_updating;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const DisableAuthButton: React.FC<{
  source: APISource;
  show_action: boolean;
}> = t0 => {
  const $ = _c(7);
  const {
    source,
    show_action
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const [is_updating, setIsUpdating] = useState(false);
  if (!show_action) {
    return null;
  }
  let t1;
  if ($[0] !== HookdeckAPI.sources || $[1] !== addToast || $[2] !== source) {
    t1 = () => {
      setIsUpdating(true);
      HookdeckAPI.sources.update(source.id, {
        ...source,
        config: {
          ...source.config,
          auth: null,
          auth_type: null
        }
      }).then(() => {
        addToast("success", "Authentication removed successfully");
      }).finally(() => {
        setIsUpdating(false);
      });
    };
    $[0] = HookdeckAPI.sources;
    $[1] = addToast;
    $[2] = source;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== is_updating || $[5] !== t1) {
    t2 = <Button outline={true} disabled={is_updating} onClick={t1}>Disable authentication</Button>;
    $[4] = is_updating;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const EnableMethodButton: React.FC<{
  source: APISource;
  method: string;
  show_action: boolean;
}> = t0 => {
  const $ = _c(10);
  const {
    source,
    method,
    show_action
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const [is_updating, setIsUpdating] = useState(false);
  if (!show_action) {
    return null;
  }
  let t1;
  if ($[0] !== HookdeckAPI.sources || $[1] !== addToast || $[2] !== method || $[3] !== source.config.allowed_http_methods || $[4] !== source.id) {
    t1 = () => {
      setIsUpdating(true);
      HookdeckAPI.sources.update(source.id, {
        allowed_http_methods: Array.from(new Set([...(source.config.allowed_http_methods as string[]), method || "POST"]))
      }).then(() => {
        addToast("success", "Method enabled successfully");
      }).finally(() => {
        setIsUpdating(false);
      });
    };
    $[0] = HookdeckAPI.sources;
    $[1] = addToast;
    $[2] = method;
    $[3] = source.config.allowed_http_methods;
    $[4] = source.id;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = method || "POST";
  let t3;
  if ($[6] !== is_updating || $[7] !== t1 || $[8] !== t2) {
    t3 = <Button outline={true} disabled={is_updating} onClick={t1}>Enable {t2}</Button>;
    $[6] = is_updating;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
const rejection_causes_breakdown: Record<RequestRejectionCause, (props: {
  request: Request;
  source: APISource;
  show_action?: boolean;
}) => {
  label: string;
  is_retryable: boolean;
  text: string | React.ReactElement;
}> = {
  SOURCE_DISABLED: ({
    source,
    show_action = true
  }) => ({
    label: rejection_cause_labels.SOURCE_DISABLED,
    is_retryable: true,
    text: <Div flex={{
      direction: 'column',
      align: 'start',
      gap: 4
    }}>
        <Text as="span">{source.name} is a disabled source. Enable the source.</Text>
        <EnableSourceButton source={source} show_action={show_action} />
      </Div>
  }),
  NO_CONNECTION: ({
    source,
    show_action = true
  }) => ({
    label: rejection_cause_labels.NO_CONNECTION,
    is_retryable: true,
    text: <Div flex={{
      direction: 'column',
      align: 'start',
      gap: 4
    }}>
        <Text as="span">
          {source.name} does not have any connections. Add a connection to the source.
        </Text>
        {show_action && <Button outline to={`/connections/new?source_id=${source.id}`}>
            Add connection
          </Button>}
      </Div>
  }),
  VERIFICATION_FAILED: ({
    source,
    show_action = true
  }) => ({
    label: rejection_cause_labels.VERIFICATION_FAILED,
    is_retryable: true,
    text: <Div flex={{
      direction: 'column',
      align: 'start',
      gap: 4
    }}>
        <Text as="span">
          The request failed authentication verification. Update your source verification
          configuration or disable the authentication.
        </Text>
        <DisableAuthButton source={source} show_action={show_action} />
      </Div>
  }),
  UNSUPPORTED_HTTP_METHOD: ({
    request,
    source,
    show_action = true
  }) => {
    const method = request?.data?.headers['x-hookdeck-original-method']?.toUpperCase();
    return {
      label: rejection_cause_labels.UNSUPPORTED_HTTP_METHOD,
      is_retryable: false,
      text: <Div flex={{
        direction: 'column',
        align: 'start',
        gap: 4
      }}>
          <Text as="span">
            The request HTTP method <code>{method}</code> is not enabled on the source associated
            with this request. Enable <code>{method}</code> to successfully route this request to
            Hookdeck.
          </Text>
          <EnableMethodButton source={source} method={method || 'POST'} show_action={show_action} />
        </Div>
    };
  },
  UNSUPPORTED_CONTENT_TYPE: ({
    request
  }) => ({
    label: rejection_cause_labels.UNSUPPORTED_CONTENT_TYPE,
    is_retryable: false,
    text: `The request was rejected because it's using an unsupported content type: ${request?.data?.headers['content-type']}.`
  }),
  UNPARSABLE_JSON: () => ({
    label: rejection_cause_labels.UNPARSABLE_JSON,
    is_retryable: false,
    text: `This request has a JSON content-type, but it was rejected because its JSON payload was unparsable. `
  }),
  PAYLOAD_TOO_LARGE: () => ({
    label: rejection_cause_labels.PAYLOAD_TOO_LARGE,
    is_retryable: false,
    text: `This request was rejected because the payload was too large.`
  }),
  INGESTION_FATAL: () => ({
    label: rejection_cause_labels.INGESTION_FATAL,
    is_retryable: false,
    text: `This request failed to be ingested by our system because of unique circumstances. The engineering team has been alerted and an HTTP 500 was returned to the sender in order to trigger any retry logic on their end. We are extremely sorry for the inconvenience and take these errors seriously.`
  }),
  UNKNOWN: () => ({
    label: rejection_cause_labels.UNKNOWN,
    is_retryable: false,
    text: `An unknown error happened.`
  })
};
const RETRYABLE_CAUSES = new Set<RequestRejectionCause>(['SOURCE_DISABLED', 'NO_CONNECTION', 'VERIFICATION_FAILED']);
export const isRejectionCauseRetryable = (cause: RequestRejectionCause): boolean => {
  return RETRYABLE_CAUSES.has(cause);
};
export const getRejectionCauseBreakdown = (cause: RequestRejectionCause) => {
  if (cause === 'UNKNOWN' || !rejection_causes_breakdown[cause]) {
    return rejection_causes_breakdown.UNKNOWN;
  }
  return rejection_causes_breakdown[cause];
};